import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { vendorsApi, engagementApi, api } from '../api/client';
import { markOnboardingStep } from '../lib/onboarding';

export default function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const [vendor, setVendor] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [engagements, setEngagements] = useState<any[]>([]);
  const [actionItems, setActionItems] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [vendorDocs, setVendorDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'submissions' | 'awards' | 'subawards' | 'messages' | 'notes' | 'engagements' | 'actions'>('submissions');
  const [awardList, setAwardList] = useState<any[]>([]);
  const [awardsLoading, setAwardsLoading] = useState(false);
  const [awardsLoaded, setAwardsLoaded] = useState(false);
  const [expandedAward, setExpandedAward] = useState<string | null>(null);
  // Subcontracting (as a sub) — lazy loaded when the tab opens.
  const [subList, setSubList] = useState<any[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsLoaded, setSubsLoaded] = useState(false);

  // Message form
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgEmail, setMsgEmail] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState('');

  // Notes form
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [vendorRes, msgRes, engRes, aiRes, subRes] = await Promise.all([
        vendorsApi.get(id).catch(() => ({ item: null })),
        engagementApi.listMessages({ vendor_id: id }).catch(() => ({ items: [] })),
        engagementApi.listEngagements({ vendor_id: id }).catch(() => ({ items: [] })),
        engagementApi.listActionItems({ vendor_id: id }).catch(() => ({ items: [] })),
        api.get<any>(`/vendors/${id}/submissions`).catch(() => ({ submissions: [], documents: [] })),
      ]);
      setVendor(vendorRes.item || vendorRes);
      markOnboardingStep('open_vendor');
      setMessages(msgRes.items || []);
      setEngagements(engRes.items || []);
      setActionItems(aiRes.items || []);
      setSubmissions(subRes.submissions || []);
      setVendorDocs(subRes.documents || []);
      // Extract notes from vendor docs
      const vendorNotes = (subRes.documents || []).filter((d: any) => d.doc_type === 'vendor_note');
      setNotes(vendorNotes);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load the per-award detail list the first time the Past Awards tab opens.
  useEffect(() => {
    if (activeTab === 'awards' && !awardsLoaded && !awardsLoading && id) {
      setAwardsLoading(true);
      vendorsApi.getAwards(id, 200)
        .then((res: any) => { setAwardList(res.awards || []); })
        .catch(() => { setAwardList([]); })
        .finally(() => { setAwardsLoading(false); setAwardsLoaded(true); });
    }
  }, [activeTab, awardsLoaded, awardsLoading, id]);

  // Lazy-load the sub-award (subcontracting) list the first time that tab opens.
  useEffect(() => {
    if (activeTab === 'subawards' && !subsLoaded && !subsLoading && id) {
      setSubsLoading(true);
      vendorsApi.getSubawards(id, 200)
        .then((res: any) => { setSubList(res.subawards || []); })
        .catch(() => { setSubList([]); })
        .finally(() => { setSubsLoading(false); setSubsLoaded(true); });
    }
  }, [activeTab, subsLoaded, subsLoading, id]);

  const formatDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleString() : '—';
  const formatDollars = (v: number) => v ? '$' + v.toLocaleString() : '—';
  const fmtMoney = (v: any) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v || '').replace(/[$,]/g, ''));
    return isNaN(n) ? '—' : '$' + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!msgSubject.trim() && !msgBody.trim()) return;
    setMsgSending(true);
    setMsgSuccess('');
    try {
      await engagementApi.sendMessage({
        vendor_id: id,
        vendor_name: vendor?.name || '',
        subject: msgSubject,
        body: msgBody,
        recipient_email: msgEmail,
        message_type: 'outreach',
        channel: msgEmail ? 'email' : 'internal',
        status: msgEmail ? 'sent' : 'draft',
      });
      setMsgSuccess('Message sent successfully');
      setMsgSubject('');
      setMsgBody('');
      setMsgEmail('');
      load();
    } catch (err: any) {
      setMsgSuccess(`Error: ${err.message}`);
    }
    setMsgSending(false);
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      await api.post('/documents', {
        opportunity_id: '',
        vendor_id: id,
        filename: '',
        doc_type: 'vendor_note',
        notes: noteText,
      });
      setNoteText('');
      load();
    } catch (err: any) {
      console.error(err);
    }
    setNoteSaving(false);
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Loading vendor details...</div>;
  if (!vendor) return <div style={{ padding: 32, color: '#dc2626' }}>Vendor not found.</div>;

  const pills = (text: string, bg: string, color: string) =>
    (text || '').split('|').filter(Boolean).map((item, i) => (
      <span key={i} style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, background: bg, color, display: 'inline-block', margin: '2px 3px' }}>
        {item.trim()}
      </span>
    ));

  const tabStyle = (tab: string): React.CSSProperties => ({
    padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
    background: 'none', color: activeTab === tab ? '#2563eb' : '#6b7280',
  });

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Back link */}
      <Link to="/vendors" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none', marginBottom: 12, display: 'inline-block' }}>← Back to Vendors</Link>

      {/* Vendor header card */}
      <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 22, margin: 0, color: '#1f2937' }}>{vendor.name}</h2>
            {vendor.dba_name && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>DBA: {vendor.dba_name}</div>}
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#6b7280', flexWrap: 'wrap' }}>
              <span><strong>UEI:</strong> <span style={{ fontFamily: 'monospace' }}>{vendor.uei || '—'}</span></span>
              <span><strong>CAGE:</strong> <span style={{ fontFamily: 'monospace' }}>{vendor.cage_code || '—'}</span></span>
              {vendor.country && <span><strong>Country:</strong> {vendor.country}</span>}
              {vendor.state && <span><strong>State:</strong> {vendor.state}</span>}
              {vendor.city && <span><strong>City:</strong> {vendor.city}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {vendor.past_awards === 'yes' && <span style={{ background: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginRight: 6 }}>🏆 Past Award</span>}
            {vendor.is_small_business && <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Small Business</span>}
            {vendor.registration_status && (
              <div style={{ marginTop: 6, fontSize: 11, color: vendor.registration_status === 'A' || vendor.registration_status === 'Z2' ? '#065f46' : '#dc2626' }}>
                {vendor.registration_status === 'A' || vendor.registration_status === 'Z2' ? '● Active' : '○ Inactive'}
              </div>
            )}
          </div>
        </div>

        {/* Info grid */}
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 12 }}>
          {vendor.primary_naics && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Primary NAICS:</span> <span style={{ fontFamily: 'monospace', color: '#1e40af' }}>{vendor.primary_naics}</span></div>
          )}
          {vendor.website && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Website:</span> <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{vendor.website}</a></div>
          )}
          {vendor.entity_structure && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Entity Type:</span> {vendor.entity_structure}</div>
          )}
          {vendor.business_start_date && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Business Start:</span> {vendor.business_start_date}</div>
          )}
          {vendor.registration_date && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>SAM Registered:</span> {vendor.registration_date}</div>
          )}
          {vendor.expiration_date && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Expires:</span> {vendor.expiration_date}</div>
          )}
          {vendor.congressional_district && (
            <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Congressional District:</span> {vendor.congressional_district}</div>
          )}
        </div>

        {/* Socio-Economic Status */}
        {vendor.socio_economic_type && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Socio-Economic Status</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {vendor.socio_economic_type.split(',').map((s: string, i: number) => (
                <span key={i} style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, background: '#f3e8ff', color: '#7c3aed', display: 'inline-block' }}>{s.trim()}</span>
              ))}
            </div>
          </div>
        )}

        {/* NAICS Codes */}
        {vendor.naics_codes && (Array.isArray(vendor.naics_codes) ? vendor.naics_codes.length > 0 : vendor.naics_codes) && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>NAICS Codes</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {(Array.isArray(vendor.naics_codes) ? vendor.naics_codes : String(vendor.naics_codes).split(',')).slice(0, 20).map((code: string, i: number) => (
                <span key={i} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: '#dbeafe', color: '#1e40af', fontFamily: 'monospace' }}>{String(code).trim()}</span>
              ))}
            </div>
          </div>
        )}

        {/* PSC Codes */}
        {vendor.psc_codes && (Array.isArray(vendor.psc_codes) ? vendor.psc_codes.length > 0 : vendor.psc_codes) && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>PSC Codes</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {(Array.isArray(vendor.psc_codes) ? vendor.psc_codes : String(vendor.psc_codes).split(',')).slice(0, 20).map((code: string, i: number) => (
                <span key={i} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: '#d1fae5', color: '#065f46', fontFamily: 'monospace' }}>{String(code).trim()}</span>
              ))}
            </div>
          </div>
        )}

        {/* SBA Business Types */}
        {vendor.sba_business_types && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>SBA Business Types</div>
            <div style={{ fontSize: 11, color: '#374151', fontFamily: 'monospace' }}>{vendor.sba_business_types}</div>
          </div>
        )}

        {/* Legacy fields (from old Carahsoft data) */}
        {vendor.description && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Industry</div>
            <div>{pills(vendor.description, '#dbeafe', '#1e40af')}</div>
          </div>
        )}
        {vendor.vendor_type && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Capabilities</div>
            <div>{pills(vendor.vendor_type, '#d1fae5', '#065f46')}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Vendor details" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 16, display: 'flex', gap: 0 }}>
        <button role="tab" id="tab-submissions" aria-selected={activeTab === 'submissions'} aria-controls="tabpanel-submissions" style={tabStyle('submissions')} onClick={() => setActiveTab('submissions')}>Submissions ({submissions.length})</button>
        <button role="tab" id="tab-awards" aria-selected={activeTab === 'awards'} aria-controls="tabpanel-awards" style={tabStyle('awards')} onClick={() => setActiveTab('awards')}>Past Awards{vendor.award_count ? ` (${vendor.award_count})` : ''}</button>
        <button role="tab" id="tab-subawards" aria-selected={activeTab === 'subawards'} aria-controls="tabpanel-subawards" style={tabStyle('subawards')} onClick={() => setActiveTab('subawards')}>Subcontracting{vendor.subaward_count ? ` (${vendor.subaward_count})` : ''}</button>
        <button role="tab" id="tab-notes" aria-selected={activeTab === 'notes'} aria-controls="tabpanel-notes" style={tabStyle('notes')} onClick={() => setActiveTab('notes')}>Notes ({notes.length})</button>
        <button role="tab" id="tab-messages" aria-selected={activeTab === 'messages'} aria-controls="tabpanel-messages" style={tabStyle('messages')} onClick={() => setActiveTab('messages')}>Communications ({messages.length})</button>
        <button role="tab" id="tab-engagements" aria-selected={activeTab === 'engagements'} aria-controls="tabpanel-engagements" style={tabStyle('engagements')} onClick={() => setActiveTab('engagements')}>Engagements ({engagements.length})</button>
        <button role="tab" id="tab-actions" aria-selected={activeTab === 'actions'} aria-controls="tabpanel-actions" style={tabStyle('actions')} onClick={() => setActiveTab('actions')}>Action Items ({actionItems.length})</button>
      </div>

      {/* Tab content */}
      {activeTab === 'submissions' && (
        <div role="tabpanel" id="tabpanel-submissions" aria-labelledby="tab-submissions">
          {submissions.length === 0 && vendorDocs.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>No submissions yet</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>When you upload vendor documents during evaluation, they will appear here linked to the opportunity.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {submissions.map((sub: any, i: number) => (
                <div key={i} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                        {sub.opportunity_title || 'Opportunity'}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                        Opportunity ID: {sub.opportunity_id}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 9999, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                        Submitted
                      </span>
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>{formatDate(sub.uploaded_at)}</div>
                    </div>
                  </div>
                  {sub.filename && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #f3f4f6' }}>
                      <span style={{ fontSize: 16 }}>📎</span>
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{sub.filename}</span>
                    </div>
                  )}
                  {sub.notes && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                      <strong>Notes:</strong> {sub.notes}
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Link to={`/opportunities/${sub.opportunity_id}`} style={{ fontSize: 11, color: '#4f46e5', textDecoration: 'none', fontWeight: 500 }}>
                      View Opportunity →
                    </Link>
                  </div>
                </div>
              ))}
              {/* Show any additional vendor docs not in submissions list */}
              {vendorDocs.filter(d => !submissions.find((s: any) => s.document_id === d.id)).map((doc: any) => (
                <div key={doc.id} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>📎</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>{doc.filename || 'Document'}</div>
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>{doc.doc_type === 'vendor_notes' ? 'Notes' : 'Evaluation Document'}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{formatDate(doc.created_at)}</span>
                  </div>
                  {doc.notes && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>{doc.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'awards' && (
        <div role="tabpanel" id="tabpanel-awards" aria-labelledby="tab-awards">
          {vendor.award_count ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Summary header — from the award rollup (by awardee UEI) */}
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 14 }}>Federal Award History</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Obligated</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#065f46', marginTop: 2 }}>
                      {'$' + Number(vendor.award_total_dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Awards Won</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1e40af', marginTop: 2 }}>
                      {Number(vendor.award_count || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 10, fontSize: 12 }}>
                  {vendor.award_top_agencies && (
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Top Agencies: </span>
                      {vendor.award_top_agencies.split(';').filter(Boolean).map((a: string, i: number) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, background: '#eef2ff', color: '#3730a3', display: 'inline-block', margin: '2px 3px' }}>{a.trim()}</span>
                      ))}
                    </div>
                  )}
                  {vendor.award_top_naics && (
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Top NAICS: </span>
                      {vendor.award_top_naics.split(';').filter(Boolean).map((n: string, i: number) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: '#dbeafe', color: '#1e40af', fontFamily: 'monospace', display: 'inline-block', margin: '2px 3px' }}>{n.trim()}</span>
                      ))}
                    </div>
                  )}
                  {vendor.award_latest_date && (
                    <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Most Recent Award: </span>{vendor.award_latest_date}</div>
                  )}
                </div>
                <div style={{ marginTop: 14, fontSize: 10, color: '#9ca3af' }}>
                  Aggregated from federal contract awards (USAspending) matched by UEI {vendor.uei}.
                </div>
              </div>

              {/* Per-award detail list (lazy-loaded from the awardee_uei index) */}
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                  Individual Awards {awardsLoaded && awardList.length ? `(showing ${awardList.length})` : ''}
                </div>
                {awardsLoading ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading awards…</div>
                ) : awardList.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {awardsLoaded ? 'No individual award records found for this UEI.' : ''}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {awardList.map((a: any, i: number) => {
                      const key = a.id || a.piid || String(i);
                      const open = expandedAward === key;
                      return (
                        <div key={key} style={{ border: '1px solid #f1f5f9', borderRadius: 6 }}>
                          <button
                            onClick={() => setExpandedAward(open ? null : key)}
                            style={{ width: '100%', textAlign: 'left', background: open ? '#f8fafc' : 'white', border: 'none', padding: '10px 12px', cursor: 'pointer', borderRadius: 6 }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {a.psc_description || a.transaction_description || a.piid || 'Award'}
                                </div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                  {a.agency || '—'}{a.date_signed ? ` · ${a.date_signed}` : ''}{a.piid ? ` · ${a.piid}` : ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>{fmtMoney(a.dollars_obligated)}</div>
                                <div style={{ fontSize: 10, color: '#9ca3af' }}>{open ? 'hide' : 'details'}</div>
                              </div>
                            </div>
                          </button>
                          {open && (
                            <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 11, color: '#374151' }}>
                              {a.total_contract_value && <div><span style={{ color: '#6b7280' }}>Contract Value: </span>{fmtMoney(a.total_contract_value)}</div>}
                              {a.sub_agency && <div><span style={{ color: '#6b7280' }}>Sub-Agency: </span>{a.sub_agency}</div>}
                              {(a.naics_code || a.naics_description) && <div><span style={{ color: '#6b7280' }}>NAICS: </span>{a.naics_code} {a.naics_description}</div>}
                              {(a.psc_code) && <div><span style={{ color: '#6b7280' }}>PSC: </span>{a.psc_code}</div>}
                              {a.award_type && <div><span style={{ color: '#6b7280' }}>Award Type: </span>{a.award_type}</div>}
                              {a.type_of_contract_pricing && <div><span style={{ color: '#6b7280' }}>Pricing: </span>{a.type_of_contract_pricing}</div>}
                              {a.extent_competed && <div><span style={{ color: '#6b7280' }}>Competition: </span>{a.extent_competed}</div>}
                              {a.number_of_offers_received && <div><span style={{ color: '#6b7280' }}>Offers Received: </span>{a.number_of_offers_received}</div>}
                              {a.type_of_set_aside && <div><span style={{ color: '#6b7280' }}>Set-Aside: </span>{a.type_of_set_aside}</div>}
                              {a.pop_current_end_date && <div><span style={{ color: '#6b7280' }}>PoP End: </span>{a.pop_current_end_date}</div>}
                              {a.pop_state && <div><span style={{ color: '#6b7280' }}>Place of Performance: </span>{a.pop_state}</div>}
                              {a.transaction_description && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6b7280' }}>Description: </span>{a.transaction_description}</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🏆</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>No federal award history on record</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>No contract awards matched this vendor's UEI. Award data comes from uploaded USAspending files.</div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'subawards' && (
        <div role="tabpanel" id="tabpanel-subawards" aria-labelledby="tab-subawards">
          {vendor.subaward_count ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Summary — from the sub-award rollup (by subawardee UEI) */}
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Subcontracting History (as a subcontractor)</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 14 }}>Work this vendor performed as a SUB under other primes — widens the picture beyond prime awards.</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Sub $</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#7c3aed', marginTop: 2 }}>
                      {'$' + Number(vendor.subaward_total_dollars || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Subawards</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#1e40af', marginTop: 2 }}>
                      {Number(vendor.subaward_count || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 10, fontSize: 12 }}>
                  {vendor.sub_top_primes && (
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>Teams under (top primes): </span>
                      {vendor.sub_top_primes.split(';').filter(Boolean).map((a: string, i: number) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, background: '#f5f3ff', color: '#6d28d9', display: 'inline-block', margin: '2px 3px' }}>{a.trim()}</span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(vendor.sub_naics_codes) && vendor.sub_naics_codes.length > 0 && (
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>NAICS performed under: </span>
                      {vendor.sub_naics_codes.slice(0, 12).map((n: string, i: number) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: '#ede9fe', color: '#5b21b6', fontFamily: 'monospace', display: 'inline-block', margin: '2px 3px' }}>{n}</span>
                      ))}
                    </div>
                  )}
                  {vendor.sub_latest_date && (
                    <div><span style={{ color: '#6b7280', fontWeight: 600 }}>Most Recent Subaward: </span>{vendor.sub_latest_date}</div>
                  )}
                </div>
                <div style={{ marginTop: 14, fontSize: 10, color: '#9ca3af' }}>
                  From USAspending sub-award reports matched by UEI {vendor.uei}. Subaward reporting is less complete than prime data — treat as directional.
                </div>
              </div>

              {/* Individual subawards (lazy-loaded) */}
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                  Individual Subawards {subsLoaded && subList.length ? `(showing ${subList.length})` : ''}
                </div>
                {subsLoading ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading subawards…</div>
                ) : subList.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    {subsLoaded ? 'No individual subaward records found for this UEI.' : ''}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {subList.map((s: any, i: number) => (
                      <div key={s.id || s.subaward_number || i} style={{ border: '1px solid #f1f5f9', borderRadius: 6, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>
                              under {s.prime_awardee_name || '—'}
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                              {s.agency || '—'}
                              {s.subaward_action_date ? ` · ${s.subaward_action_date}` : ''}
                              {s.prime_piid ? ` · prime ${s.prime_piid}` : ''}
                              {s.prime_naics_code ? ` · NAICS ${s.prime_naics_code}` : ''}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>{fmtMoney(s.subaward_amount)}</div>
                            {s.usaspending_permalink && (
                              <a href={s.usaspending_permalink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#4338ca', textDecoration: 'none' }}>view prime ↗</a>
                            )}
                          </div>
                        </div>
                        {s.subaward_description && (
                          <div style={{ fontSize: 11, color: '#374151', marginTop: 6 }}>{s.subaward_description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>No subcontracting history on record</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>No sub-award records matched this vendor's UEI. Sub-award data comes from uploaded USAspending sub-award files.</div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'messages' && (
        <div role="tabpanel" id="tabpanel-messages" aria-labelledby="tab-messages">
          {/* Send Message Form */}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Send Message</div>
            <form onSubmit={handleSendMessage}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                <input
                  placeholder="Subject"
                  value={msgSubject}
                  onChange={e => setMsgSubject(e.target.value)}
                  aria-label="Message subject"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
                />
                <input
                  placeholder="Email (optional — leave empty for internal note)"
                  value={msgEmail}
                  onChange={e => setMsgEmail(e.target.value)}
                  aria-label="Recipient email"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
                />
              </div>
              <textarea
                placeholder="Message body..."
                value={msgBody}
                onChange={e => setMsgBody(e.target.value)}
                rows={3}
                aria-label="Message body"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                {msgSuccess && <span role="status" aria-live="polite" style={{ fontSize: 11, color: msgSuccess.startsWith('Error') ? '#dc2626' : '#059669' }}>{msgSuccess}</span>}
                <button
                  type="submit"
                  disabled={msgSending || (!msgSubject.trim() && !msgBody.trim())}
                  style={{
                    marginLeft: 'auto',
                    padding: '8px 16px',
                    background: msgSending ? '#94a3b8' : '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: msgSending ? 'default' : 'pointer',
                  }}
                >
                  {msgSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>

          {/* Message History */}
          {messages.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              No communications with this vendor yet. Use the form above to send one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {messages.map((msg: any) => (
                <div key={msg.id} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{msg.subject || '(No subject)'}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 9999, background: msg.status === 'sent' ? '#d1fae5' : msg.status === 'failed' ? '#fee2e2' : '#fef3c7', color: msg.status === 'sent' ? '#065f46' : msg.status === 'failed' ? '#991b1b' : '#92400e', fontWeight: 600 }}>
                        {msg.status}
                      </span>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(msg.created_at)}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    To: {msg.recipient_email || '—'} | Type: {msg.message_type || '—'} | Channel: {msg.channel || 'email'}
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 8, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {msg.body || msg.body_with_signature || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div role="tabpanel" id="tabpanel-notes" aria-labelledby="tab-notes">
          {/* Add Note Form */}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Add Note</div>
            <form onSubmit={handleAddNote}>
              <textarea
                placeholder="Write a note about this vendor (internal, not sent to vendor)..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={3}
                aria-label="Vendor note"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  type="submit"
                  disabled={noteSaving || !noteText.trim()}
                  style={{
                    padding: '8px 16px',
                    background: noteSaving || !noteText.trim() ? '#94a3b8' : '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: noteSaving || !noteText.trim() ? 'default' : 'pointer',
                  }}
                >
                  {noteSaving ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </form>
          </div>

          {/* Notes List */}
          {notes.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              No notes yet. Use the form above to add one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {notes.map((note: any) => (
                <div key={note.id} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', flex: 1 }}>
                      {note.notes}
                    </div>
                    <span style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap', marginLeft: 12 }}>{formatDate(note.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'engagements' && (
        <div role="tabpanel" id="tabpanel-engagements" aria-labelledby="tab-engagements">
          {engagements.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              No engagements recorded with this vendor yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {engagements.map((eng: any) => (
                <div key={eng.id} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{eng.subject || eng.type || 'Engagement'}</div>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 9999, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                      {eng.type}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    {formatDate(eng.date)} | Status: {eng.status || '—'} {eng.follow_up_required && '| Follow-up required'}
                  </div>
                  {eng.notes && <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>{eng.notes}</div>}
                  {eng.outcome && <div style={{ fontSize: 12, color: '#065f46', marginTop: 4 }}><strong>Outcome:</strong> {eng.outcome}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'actions' && (
        <div role="tabpanel" id="tabpanel-actions" aria-labelledby="tab-actions">
          {actionItems.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              No action items for this vendor.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {actionItems.map((ai: any) => (
                <div key={ai.id} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{ai.title}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 9999, fontWeight: 600,
                        background: ai.priority === 'urgent' ? '#fee2e2' : ai.priority === 'high' ? '#fef3c7' : '#f3f4f6',
                        color: ai.priority === 'urgent' ? '#991b1b' : ai.priority === 'high' ? '#92400e' : '#6b7280',
                      }}>{ai.priority}</span>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 9999, fontWeight: 600,
                        background: ai.status === 'completed' ? '#d1fae5' : ai.status === 'in_progress' ? '#dbeafe' : '#fef3c7',
                        color: ai.status === 'completed' ? '#065f46' : ai.status === 'in_progress' ? '#1e40af' : '#92400e',
                      }}>{ai.status}</span>
                    </div>
                  </div>
                  {ai.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{ai.description}</div>}
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                    Assigned: {ai.assigned_to || '—'} | Due: {ai.due_date ? formatDate(ai.due_date) : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
